import { cn } from "@garther/ui/libs";
import React from "react";

export interface TabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  variant?: "default" | "pills" | "underline";
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function Tabs({
  items,
  activeTab,
  onTabChange,
  variant = "default",
  size = "md",
  className = "",
}: TabsProps) {
  const baseClasses =
    "inline-flex items-center justify-center font-medium transition-all duration-200";

  const sizeClasses = {
    sm: "px-3 py-1.5 text-responsive-sm",
    md: "px-4 py-2 text-responsive-base",
    lg: "px-6 py-3 text-responsive-lg",
  };

  const variantClasses = {
    default: {
      active: "bg-background-dark text-text-inverse ",
      inactive:
        "bg-background-highlight text-text-secondary hover:bg-background-dark hover:text-text-inverse",
      disabled: "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed",
    },
    pills: {
      active: "bg-background-dark text-text-inverse",
      inactive:
        "bg-background-primary text-text-secondary border-gray-200 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300",
      disabled: "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed",
    },
    underline: {
      active:
        "border-b-2 border-border-dark text-text-primary font-normal pb-1",
      inactive:
        "border-b-2 border-transparent text-text-secondary font-normal hover:text-text-primary hover:border-text-primary hover:font-medium pb-1",
      disabled:
        "border-b-2 border-transparent text-text-secondary font-normal cursor-not-allowed pb-1",
    },
  };

  const containerClasses = {
    default: "flex space-x-1 p-1 bg-transparent rounded-lg",
    pills: "flex space-x-1 p-1 bg-gray-50 rounded-lg",
    underline: "border-b border-gray-200",
  };

  return (
    <div className={`${containerClasses[variant]} ${className}`}>
      {items.map((item) => {
        const isActive = activeTab === item.id;
        const isDisabled = item.disabled;

        let buttonClasses = `${baseClasses} ${sizeClasses[size]}`;

        if (isDisabled) {
          buttonClasses += ` ${variantClasses[variant].disabled}`;
        } else if (isActive) {
          buttonClasses += ` ${variantClasses[variant].active}`;
        } else {
          buttonClasses += ` ${variantClasses[variant].inactive}`;
        }

        // Add border classes for non-underline variants
        if (variant !== "underline") {
          buttonClasses += " rounded-sm";
        }

        return (
          <button
            key={item.id}
            onClick={() => !isDisabled && onTabChange(item.id)}
            disabled={isDisabled}
            className={cn(buttonClasses)}
            aria-selected={isActive}
            role="tab"
          >
            {item.icon && (
              <span className="mr-2 flex-shrink-0">{item.icon}</span>
            )}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  children: React.ReactNode;
  isActive: boolean;
  className?: string;
}

export function TabPanel({
  children,
  isActive,
  className = "",
}: TabPanelProps) {
  if (!isActive) return null;

  return (
    <div className={`${className}`} role="tabpanel" aria-hidden={!isActive}>
      {children}
    </div>
  );
}

export interface TabGroupProps {
  items: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  variant?: "default" | "pills" | "underline";
  size?: "sm" | "md" | "lg";
  className?: string;
  children: React.ReactNode;
}

export function TabGroup({
  items,
  activeTab,
  onTabChange,
  variant = "default",
  size = "md",
  className = "",
  children,
}: TabGroupProps) {
  return (
    <div className={cn("", className)} role="tablist">
      <Tabs
        items={items}
        activeTab={activeTab}
        onTabChange={onTabChange}
        variant={variant}
        size={size}
      />
      <div className="mt-4 sm:mt-6">{children}</div>
    </div>
  );
}
